/**
 * A failure of the platform, in words a person at the shop can read (contract v10.31).
 *
 * The back no longer sends the internal text of a failure (the model, the provider's
 * code) to anybody but a superadministrator: it sends a stable code, `LEC-01`, `TRI-02`,
 * `ING-09` (`cubas.domain.failures` of the back, catalogue in its `docs/architecture.md`,
 * «Códigos de error»). Here is what each one means for the person looking at the screen.
 * The code itself is shown too, small: it is what they read out to support, and what
 * support finds in Administración → Incidencias.
 *
 * Eloy, 2026-10-02, after the screen of a quote printed `lectura: gemini-3.8-flash: el
 * proveedor respondio 429 RESOURCE_EXHAUSTED`: «el cliente no debe tener la información
 * interna, como mucho un código».
 */
import type { EmailDetail } from './types';

export type AutoRetry = NonNullable<EmailDetail['auto_retry']>;

type Family = 'TRI' | 'LEC' | 'ING';

const SUBJECT: Record<Family, string> = {
  TRI: 'El servicio que clasifica los correos',
  LEC: 'El servicio que lee los documentos',
  ING: 'El sistema',
};

const KIND: Record<string, (subject: string) => string> = {
  '01': (subject) => `${subject} estaba saturado en ese momento.`,
  '02': (subject) => `${subject} tardó demasiado en responder.`,
  '03': (subject) => `${subject} no estaba disponible en ese momento.`,
  '04': () =>
    'El documento no se pudo abrir: puede estar protegido con contraseña, dañado o ser demasiado grande.',
  '05': () => 'Se leyó el documento, pero no se pudieron guardar las líneas.',
};

/** 01, 02 and 03 pass on their own: the platform retries them. 04 and 05 do not. */
const PASSING = new Set(['01', '02', '03']);

/** «LEC-01» → «El servicio que lee los documentos estaba saturado en ese momento.» */
export function failureText(code: string | null | undefined): string {
  const [family, kind] = (code ?? '').split('-') as [Family | undefined, string | undefined];
  if (family === 'ING') return 'No se pudo guardar este correo en la aplicación.';
  const subject = family && family in SUBJECT ? SUBJECT[family] : null;
  const sentence = subject && kind ? KIND[kind] : undefined;
  if (sentence && subject) return sentence(subject);
  if (family === 'LEC') return 'No se pudo leer el documento.';
  if (family === 'TRI') return 'No se pudo clasificar este correo.';
  return 'Algo ha fallado al procesarlo.';
}

/** Whether waiting fixes it: the provider was full, slow or down. */
export function failurePasses(code: string | null | undefined): boolean {
  return PASSING.has((code ?? '').split('-')[1] ?? '');
}

const timeFormat = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' });

/**
 * What the automatic retries are doing, as one sentence: the next attempt, or that it
 * stopped after a day. The time is local and rounded to the minute: the round runs
 * once a minute.
 */
export function retryLine(retry: AutoRetry): string {
  if (retry.exhausted || !retry.next_at) {
    return 'Lo hemos intentado solos durante un día sin respuesta: ya no se reintenta solo.';
  }
  const at = timeFormat.format(new Date(retry.next_at));
  const tries = retry.failures === 1 ? 'Ha fallado una vez' : `Ha fallado ${retry.failures} veces`;
  return `${tries}; lo estamos intentando de nuevo solos. Próximo intento hacia las ${at}.`;
}

/** Retried right now, as opposed to failed for good. */
export function isRetrying(
  retry: EmailDetail['auto_retry'] | undefined,
): retry is AutoRetry & { exhausted: false } {
  return Boolean(retry && !retry.exhausted);
}
